import { ApiError, apiFetchOrThrow } from './client'

export interface AuthUser {
  id: string
  /**
   * Null when Sign in with Apple withheld the address: Apple only releases it
   * on the FIRST authorization, and the Apple sub is the identity on its own.
   */
  email: string | null
  name: string | null
  avatarUrl?: string | null
  createdAt: string
  /**
   * The Account screen's newsletter toggle, sent by `GET`/`PATCH /auth/me`.
   * Absent on a user cached before it existed, or from login (until `getMe`).
   */
  newsletter?: { subscribed: boolean; available: boolean }
}

// Auth is now cookie-only (httpOnly caliu_session cookie set by the server).
// No localStorage tokens — reduces XSS attack surface.
// Legacy token cleanup: remove any leftover token from previous versions.
if (typeof localStorage !== 'undefined') {
  localStorage.removeItem('caliu_session_token')
}

export async function login(email: string, password: string): Promise<AuthUser> {
  const data = await apiFetchOrThrow<{ user: AuthUser; sessionId: string }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
  // Session cookie is set automatically by the server response (httpOnly, secure)
  return data.user
}

export async function register(
  email: string,
  password: string,
  name?: string,
  next?: string | null,
  locale?: string,
): Promise<{ ok: boolean; message: string }> {
  return apiFetchOrThrow<{ ok: boolean; message: string }>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({
      email,
      password,
      name,
      next: next ?? undefined,
      locale: locale ?? undefined,
    }),
  })
}

export async function logout(): Promise<void> {
  await apiFetchOrThrow<{ ok: boolean }>('/auth/logout', { method: 'POST' })
}

export async function getMe(): Promise<AuthUser | null> {
  try {
    const data = await apiFetchOrThrow<{ user: AuthUser }>('/auth/me')
    return data.user
  } catch (err) {
    // Null means DEFINITIVELY signed out (the server saw the request and
    // rejected the session). Anything else (offline, 5xx) rethrows so callers
    // keep trusting local state — a transient failure must never read as a
    // sign-out.
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      return null
    }
    throw err
  }
}

/** Update the current user's display name. Pass `null` to clear it. */
export async function updateMe(name: string | null): Promise<AuthUser> {
  const data = await apiFetchOrThrow<{ user: AuthUser }>('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  })
  return data.user
}
